import { Inject, Injectable, OnDestroy, DOCUMENT } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { from, Observable, Subscription, throwError } from 'rxjs';
import { map, catchError, finalize, switchMap } from 'rxjs/operators';
import { NavController } from '@ionic/angular';
import { LoadingService } from '../loading-service/loading.service';
import { Capacitor } from '@capacitor/core';
import { App, AppInfo } from '@capacitor/app';
import { ILocalAppEnvironment } from '../environment-config-service/environment-config-service';
import { UserData } from './models/user-data.model';
import { TokenService } from '../token/token.service';
import { Events } from '../events/events';
import { sharedConfig } from 'src/environments/shared-config';
import { entraExternalDRefreshTokenPost } from 'src/app/api/proxy/authentication/fn/entra-external-d/entra-external-d-refresh-token-post';
import { entraExternalDCookieUserInfoGet } from 'src/app/api/proxy/authentication/fn/entra-external-d/entra-external-d-cookie-user-info-get';
import { entraExternalDLogoutCookiePost } from 'src/app/api/proxy/authentication/fn/entra-external-d/entra-external-d-logout-cookie-post';
import { entraExternalDRegisterUserFromPreRegistrationPost } from 'src/app/api/proxy/authentication/fn/entra-external-d/entra-external-d-register-user-from-pre-registration-post';
import { entraExternalDRequestOtpPost } from 'src/app/api/proxy/authentication/fn/entra-external-d/entra-external-d-request-otp-post';
import { entraExternalDVerifyOtpAsyncPost } from 'src/app/api/proxy/authentication/fn/entra-external-d/entra-external-d-verify-otp-async-post';
import { entraExternalDForgotPasswordRequestPost } from 'src/app/api/proxy/authentication/fn/entra-external-d/entra-external-d-forgot-password-request-post';
import { entraExternalDResetForgotPasswordPost } from 'src/app/api/proxy/authentication/fn/entra-external-d/entra-external-d-reset-forgot-password-post';
import { appEnvironmentCountriesGet } from 'src/app/api/proxy/environment/fn/app-environment/app-environment-countries-get';
import { appEnvironmentSettingsPost } from 'src/app/api/proxy/environment/fn/app-environment/app-environment-settings-post';
import { appEnvironmentEnvironmentSetupEnvironmentNameCountryCodeDeviceTypeNameAppIdVersionBuildProductionGet } from 'src/app/api/proxy/environment/fn/app-environment/app-environment-environment-setup-environment-name-country-code-device-type-name-app-id-version-build-production-get';
import { entraExternalDCreateOtpFromLoginPost } from 'src/app/api/proxy/authentication/fn/entra-external-d/entra-external-d-create-otp-from-login-post';
import { entraExternalDLoginUserPost } from 'src/app/api/proxy/authentication/fn/entra-external-d/entra-external-d-login-user-post';
import { entraExternalDVerifyOtpAndSignInPost } from 'src/app/api/proxy/authentication/fn/entra-external-d/entra-external-d-verify-otp-and-sign-in-post';
import { entraExternalDExchangeBridgeTokenCodeGet } from 'src/app/api/proxy/authentication/fn/entra-external-d/entra-external-d-exchange-bridge-token-code-get';
import { clinicGroupLinkRequestRespondPost } from 'src/app/api/proxy/practice/fn/clinic-group/clinic-group-link-request-respond-post';
import { preRegistrationPost } from 'src/app/api/proxy/practice/fn/pre-registration/pre-registration-post';
import { facilityLinkPractitionerPost } from 'src/app/api/proxy/practice/fn/facility/facility-link-practitioner-post';
import { facilityUnLinkPractitionerPost } from 'src/app/api/proxy/practice/fn/facility/facility-un-link-practitioner-post';

@Injectable()
export class GlobalHttpInterceptorService implements HttpInterceptor, OnDestroy {
  appInfo?: AppInfo | null = null;
  appInfoLoaded = false;
  environment: ILocalAppEnvironment | null = null;
  currentUser: UserData | null = null;
  private isRefreshing = false; // Flag to check if token refresh is happening
  // BehaviorSubject to track whether all services are initialized
  private subscriptions: Subscription[] = [];
  private refreshPromise: Promise<string | null> | null = null;

  constructor(
    public navCtrl: NavController,
    private _TokenService: TokenService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.subscriptions.push(Events.environmentLoaded.subscribe(async (env: ILocalAppEnvironment) => {
      this.environment = env;
    }));
    this.subscriptions.push(Events.userUpdated.subscribe(async (usr: UserData | null) => {
      this.currentUser = usr;
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((a) => { a.unsubscribe() });
  }

  // ───────────────────────────── intercept ─────────────────────────────

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    if (this.isIgnoredUrl(req.url)) {
      return next.handle(req);
    }

    if (this.isPublicUrl(req.url)) {
      return from(this.addHeaders(req, null /*no token*/, true /*public*/))
        .pipe(switchMap((cloned) => this.handleRequest(cloned, next)));
    }

    if (this.isCookieAuthUrl(req.url)) {
      return from(this.addHeaders(req, null /*no bearer*/, true /*public*/)).pipe(
        switchMap((cloned) => this.handleRequest(cloned.clone({ withCredentials: true }), next, false))
      );
    }

    return from(this.buildPrivateRequest(req)).pipe(
      switchMap(({ request, hadAuth }) =>
        this.handleRequest(request, next, hadAuth)
      )
    );
  }

  /** Constructs a private request. Adds Authorization if available; otherwise none. */
  private async buildPrivateRequest(req: HttpRequest<any>): Promise<{ request: HttpRequest<any>; hadAuth: boolean; }> {
    // try to get a token (do NOT force-refresh here)
    let token: string | null = null;
    try {
      token = await this._TokenService.getTokenAsync();
    } catch {
      token = null;
    }

    // if no token yet, still proceed without auth (as you requested)
    const hadAuth = !!token;
    const requestWithHeaders = await this.addHeaders(req, token, false /*public*/);
    const cloned = requestWithHeaders.clone({ withCredentials: true });

    return { request: cloned, hadAuth };
  }

  // ─────────────────────────── helpers/core ───────────────────────────

  /** Wraps the actual HTTP call with loader + 401 handling. */
  private handleRequest(
    request: HttpRequest<any>,
    next: HttpHandler,
    hadAuth = false
  ): Observable<HttpEvent<any>> {

    if (this.isLoaderEnabledUrl(request.url) && !LoadingService.isTokenRefreshActive()) {
      LoadingService.setLoading(true, request.url);
    }

    return next.handle(request).pipe(
      map((evt) => {
        if (evt instanceof HttpResponse) {
          // success path hook if needed
        }
        return evt as HttpEvent<any>;
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          // If we never had Authorization on this request (no session), skip refresh flow.
          if (!hadAuth) {
            if (this.isCookieAuthUrl(request.url)) {
              return throwError(() => error);
            }
            return from(this._TokenService.logout('401 on unauthenticated request')).pipe(
              switchMap(() => throwError(() => error))
            );
          }
          return this.handle401Error(request, next);
        }
        return throwError(() => error);
      }),
      finalize(() => {
        if (this.isLoaderEnabledUrl(request.url)) {
          LoadingService.setLoading(false, request.url);
        }
      })
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // ─────────────────────────── 401 handling ───────────────────────────

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // do not refresh if this is the refresh endpoint itself (avoid loops)
    const refreshPath = this.extractUrlNoParams(entraExternalDRefreshTokenPost.PATH);
    if (request.url.includes(refreshPath)) {
      return throwError(() => new Error('401 on refresh endpoint'));
    }

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshPromise = this._TokenService.refreshTokenAsync();

      if (!this.refreshPromise) {
        this.isRefreshing = false;
        return throwError(() => new Error('No refresh promise created'));
      }

      return from(this.refreshPromise).pipe(
        switchMap((newToken) => {
          this.isRefreshing = false;
          this.refreshPromise = null;

          if (!newToken) {
            return throwError(() => new Error('Token refresh returned null'));
          }

          return from(this.addHeaders(request, newToken, false)).pipe(
            switchMap((cloned) => next.handle(cloned))
          );
        }),
        catchError((err) => {
          this.isRefreshing = false;
          this.refreshPromise = null;

          this._TokenService.logout('401 during token refresh').finally(() => {
          });

          return throwError(() => err)
        })
      );
    } else {
      // coalesce concurrent 401s while a refresh is in progress
      return from(this.refreshPromise!).pipe(
        switchMap((newToken) => {
          if (!newToken) {
            return throwError(() => new Error('Coalesced refresh returned null'));
          }
          return from(this.addHeaders(request, newToken, false)).pipe(
            switchMap((cloned) => next.handle(cloned))
          );
        }),
        catchError((err) => throwError(() => err))
      );
    }
  }

  // ───────────────────────── headers & utils ─────────────────────────

  /** Adds headers. If `isPublic`, never adds Authorization. */
  private async addHeaders(
    request: HttpRequest<any>,
    token: string | null,
    isPublic: boolean
  ): Promise<HttpRequest<any>> {
    // default headers
    let headers = request.headers
      .set('user-language', this.document.documentElement.lang)
      .set('ngrok-skip-browser-warning', 'true');

    // app info (cached)
    if (!this.appInfo) {
      this.appInfo = (Capacitor.getPlatform() !== 'web')
        ? await App.getInfo()
        : {
          name: window.location.hostname,
          id: sharedConfig.webAppId!,
          version: sharedConfig.version,
          build: sharedConfig.build.toString(),
        };
    }

    // app/env headers (safe for both public & private)
    if (this.appInfo && this.environment?.countryCode) {
      headers = headers
        .set('ion-app-deviceType', Capacitor.getPlatform())
        .set('ion-app-name', this.appInfo.name)
        .set('ion-app-id', this.appInfo.id)
        .set('ion-app-version', this.appInfo.version)
        .set('ion-app-build', this.appInfo.build)
        .set('ion-app-countryCode', this.environment.countryCode);
    }

    // Authorization only on private requests *and* only if a token was provided
    if (!isPublic && token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return request.clone({ headers });
  }

  private isCookieAuthUrl(url: string): boolean {
    const result = [
      this.extractUrlNoParams(entraExternalDCookieUserInfoGet.PATH),
      this.extractUrlNoParams(entraExternalDLogoutCookiePost.PATH),
    ];
    return result.some((p) => url.includes(p));
  }

  private isPublicUrl(url: string): boolean {
    const result = [
      this.extractUrlNoParams(entraExternalDRegisterUserFromPreRegistrationPost.PATH),
      this.extractUrlNoParams(entraExternalDRequestOtpPost.PATH),
      this.extractUrlNoParams(entraExternalDVerifyOtpAsyncPost.PATH),
      this.extractUrlNoParams(entraExternalDForgotPasswordRequestPost.PATH),
      this.extractUrlNoParams(entraExternalDResetForgotPasswordPost.PATH),
      this.extractUrlNoParams(appEnvironmentCountriesGet.PATH),
      this.extractUrlNoParams(appEnvironmentSettingsPost.PATH),
      this.extractUrlNoParams(clinicGroupLinkRequestRespondPost.PATH),
    ];
    return result.some((p) => url.includes(p));
  }

  private isIgnoredUrl(url: string): boolean {
    const result: string[] = [
      '/AppLogMessage',
      '/i18n/',
      this.extractUrlNoParams(appEnvironmentSettingsPost.PATH),
      this.extractUrlNoParams(appEnvironmentCountriesGet.PATH),
      this.extractUrlNoParams(appEnvironmentEnvironmentSetupEnvironmentNameCountryCodeDeviceTypeNameAppIdVersionBuildProductionGet.PATH),
      this.extractUrlNoParams(entraExternalDRefreshTokenPost.PATH),
      'https://api.onesignal.com/',
    ];
    return result.some((p) => url.includes(p));
  }

  private isLoaderEnabledUrl(url: string): boolean {
    const result: string[] = [
      this.extractUrlNoParams(entraExternalDRegisterUserFromPreRegistrationPost.PATH),
      this.extractUrlNoParams(entraExternalDRequestOtpPost.PATH),
      this.extractUrlNoParams(entraExternalDCreateOtpFromLoginPost.PATH),
      this.extractUrlNoParams(entraExternalDVerifyOtpAsyncPost.PATH),
      this.extractUrlNoParams(entraExternalDForgotPasswordRequestPost.PATH),
      this.extractUrlNoParams(entraExternalDResetForgotPasswordPost.PATH),
      this.extractUrlNoParams(entraExternalDLoginUserPost.PATH),
      this.extractUrlNoParams(entraExternalDVerifyOtpAndSignInPost.PATH),
      this.extractUrlNoParams(entraExternalDLogoutCookiePost.PATH),
      this.extractUrlNoParams(entraExternalDExchangeBridgeTokenCodeGet.PATH),
      this.extractUrlNoParams(preRegistrationPost.PATH),
      this.extractUrlNoParams(facilityLinkPractitionerPost.PATH),
      this.extractUrlNoParams(facilityUnLinkPractitionerPost.PATH),
    ];
    return result.some((p) => url.includes(p));
  }

  private extractUrlNoParams(url: string): string {
    const idx = url.indexOf('{');
    return idx === -1 ? url : url.substring(0, idx);
  }
}